/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { TableProps } from "interfaces";
import {
  HeaderEnum,
  InsuranceInterface,
  PatientInsuranceInterface,
  PatientInterface,
  QueryResultInterface,
} from "medicalsystem-shared";
import { Dispatch, FC, SetStateAction, SyntheticEvent, useEffect, useMemo, useState } from "react";

import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import Box from "components/generals/Box";
import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { AutoCompleteOption, EntityPermissions, PermissionsUser } from "types";
import Autocomplete from "components/generals/Autocomplete";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import { Method } from "axios";
import { isEmpty } from "utils/string.utils";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { createNoInsuranceOption } from "utils/model.utils";
import { useMaterialUIController } from "context/ThemeContext";
import { NO_INSURANCE_ID, NO_INSURANCE_OPTION_ID } from "constants/insurance.const";
import PercentageInput from "components/generals/PercentageInput";

/**
 * Extended properties for the InsurancePatientTable component.
 * 
 * @interface InsurancePatientTableInterface
 * @extends {TableProps<InsurancePatientTableInterface>}
 * @property {Partial<PatientInterface>} [patientSelected] - Currently selected patient
 * @property {Function} [setInsuranceRelationList] - Callback to update insurance relations
 * @property {Function} [setBoolNextButton] - Callback to enable/disable next button based on validation
 */
interface InsurancePatientTableInterface extends TableProps<InsurancePatientTableInterface> {
  patientSelected?: Partial<PatientInterface>;
  setInsuranceRelationList?: Dispatch<SetStateAction<PatientInsuranceInterface[]>>;
  setBoolNextButton?: Dispatch<SetStateAction<boolean>>;
}

/**
 * Insurance patient table component for managing patient insurance relationships.
 * Editable table for adding/removing patient insurance policies.
 * 
 * Features:
 * - Add insurance via autocomplete dropdown
 * - Remove insurance with delete icon button
 * - Edit coverage limit (percentage input)
 * - Validate policy ID and coverage limit
 * - Control next button state based on validation
 * - Fixed height table (250px) with scrolling
 * - Supports dark mode via theme controller
 * - Permission-based delete button visibility
 * - Tracks base list vs current list for change detection
 * 
 * Validation Rules:
 * - Policy ID cannot be empty
 * - Coverage limit must be valid number (not NaN)
 * - Next button disabled if validation fails
 * 
 * Workflow:
 * 1. Load available insurances and patient's current insurances
 * 2. User selects insurance from autocomplete
 * 3. New relation added to table with empty policy ID
 * 4. User fills policy ID and coverage limit
 * 5. Validation runs, controls next button
 * 6. Parent receives updated relation list
 * 
 * @param {InsurancePatientTableInterface} props - Component properties
 * @param {Partial<PatientInterface>} props.patientSelected - Patient to manage insurance for
 * @param {Function} props.setInsuranceRelationList - Callback to update insurance relations
 * @param {Function} props.setBoolNextButton - Callback to control next button state
 * 
 * @returns {JSX.Element} Editable insurance table with validation
 */
const InsurancePatientTable: FC<InsurancePatientTableInterface> = ({
  patientSelected,
  setInsuranceRelationList,
  setBoolNextButton,
}) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [insuranceList, setInsuranceList] = useState<InsuranceInterface[]>([]);
  const [insuranceSelectList, setInsuranceSelectList] = useState<PatientInsuranceInterface[]>([]);
  const [insuranceBaseList, setInsuranceBaseList] = useState<PatientInsuranceInterface[]>([]);
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  useEffect(() => {
    sendRequest(CoreAPI_URLs.INSURANCE_GET_ALL, onGetInsuranceHandler, "GET");
    if (patientSelected) {
      sendRequest(
        CoreAPI_URLs.PATIENT_INSURANCE_GET_ALL,
        onGetInsuranceRelationsHandler,
        "GET",
        null,[{ header: HeaderEnum.PATIENT_ID, value: patientSelected.id }]
      );
    }
  }, [patientSelected]);

  useEffect(() => {
    if (setInsuranceRelationList) {
      const realInsurances = insuranceSelectList.filter(
        (relation) => relation.insurance?.id !== NO_INSURANCE_OPTION_ID
      );
      setInsuranceRelationList(realInsurances);
    }

    if (setBoolNextButton) {
      setBoolNextButton(validateDataList());
    }
  }, [insuranceSelectList]);

  const onGetInsuranceHandler = (insuranceResponse: QueryResultInterface<InsuranceInterface>) => {
    setInsuranceList(insuranceResponse.results);
  };

  const validateDataList = () => {
    return insuranceSelectList.some((insurance) => {
      if (insurance.insurance?.id === NO_INSURANCE_OPTION_ID) {
        return false;
      }
      return isEmpty(insurance.policyId) || !isFinite(insurance.coverageLimit) || insurance.coverageLimit === 0;
    });
  };

  const onGetInsuranceRelationsHandler = (
    relationsList: QueryResultInterface<PatientInsuranceInterface>
  ) => {
    if (relationsList.results.length > 0) {
      setInsuranceSelectList(relationsList.results);
      setInsuranceBaseList(relationsList.results);
    } else {
      setInsuranceSelectList([createNoInsuranceOption(patientSelected)]);
    }
  };

  const insuranceOptions = useMemo<AutoCompleteOption[]>(() => {
    const noInsuranceOption: AutoCompleteOption = {
      label: t("insurancePatientTable.noInsurance"),
      id: NO_INSURANCE_ID,
    };

    if (insuranceList.length === 0) {
      return [noInsuranceOption];
    } else {
      const mappedOptions = insuranceList
        .map((a) => ({
          label: `${a.name} - ${a.payerId} , ${a.typeScheme}`.toUpperCase(),
          id: a.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      
      return [noInsuranceOption, ...mappedOptions];
    }
  }, [insuranceList, t]);

  const onSelectInsuranceHandler = async (
    _event: SyntheticEvent<Element, Event>,
    newValue: AutoCompleteOption
  ) => {
    if (newValue) {
      if (newValue.id === NO_INSURANCE_ID) {
        const hasNoInsurance = insuranceSelectList.some(
          (relation) => relation.insurance?.id === NO_INSURANCE_OPTION_ID
        );
        if (!hasNoInsurance) {
          setInsuranceSelectList([createNoInsuranceOption(patientSelected)]);
        }
        return;
      }

      const selectedInsurance = insuranceList.find((p) => p.id === newValue.id);
      if (selectedInsurance) {
        const exists = insuranceSelectList.some(
          (relation) => relation.insurance.id === selectedInsurance.id
        );
        if (!exists) {
          const newRelation: PatientInsuranceInterface = {
            patient: patientSelected || null,
            insurance: selectedInsurance,
            policyId: "",
            coPay: 0,
            coInsurance: 0,
          };
          setInsuranceSelectList((prevList) => {
            const filtered = prevList.filter(
              (relation) => relation.insurance?.id !== NO_INSURANCE_OPTION_ID
            );
            return [...filtered, newRelation];
          });
        }
      }
    }
  };

  const handleCopayChange = (copayString: string, insuranceRelation: PatientInsuranceInterface) => {
    const updatedList = insuranceSelectList.map((item) =>
      item.insurance.id === insuranceRelation.insurance.id
        ? { ...item, coPay: parseFloat(copayString) || 0 }
        : item
    );
    setInsuranceSelectList(updatedList);
  };

  const handleCoverageLimitChange = (coverageLimitString: string, insuranceRelation: PatientInsuranceInterface) => {
  const updatedList = insuranceSelectList.map((item) =>
    item.insurance.id === insuranceRelation.insurance.id
      ? { ...item, coverageLimit: parseFloat(coverageLimitString) || 0 }
      : item
  );
  setInsuranceSelectList(updatedList);
  };

  const handleCoInsuranceChange = (value: number, insuranceRelation: PatientInsuranceInterface) => {
    const updatedList = insuranceSelectList.map((item) =>
      item.insurance.id === insuranceRelation.insurance.id
        ? { ...item, coInsurance: value }
        : item
    );
    setInsuranceSelectList(updatedList);
  };


  const handlePolicyChange = (
    policyString: string,
    insuranceRelation: PatientInsuranceInterface
  ) => {
    const updatedList = insuranceSelectList.map((item) =>
      item.insurance.id === insuranceRelation.insurance.id
        ? { ...item, policyId: policyString }
        : item
    );
    setInsuranceSelectList(updatedList);
  };

  const onDeleteInsuranceHandler = async (insuranceRelation: PatientInsuranceInterface) => {
    if (insuranceRelation.insurance?.id !== NO_INSURANCE_OPTION_ID) {
      if (patientSelected?.id && insuranceRelation.id) {
        const request = await sendRequest(
          CoreAPI_URLs.PATIENT_INSURANCE_GENERAL_OPERATION,
          null,
          "GET",
          null,
          [{ header: HeaderEnum.RESOURCE_ID, value: insuranceRelation.id }]
        );
        if (request?.data) {
          await sendRequest(CoreAPI_URLs.PATIENT_INSURANCE_GENERAL_OPERATION, null, "DELETE", null, [
            { header: HeaderEnum.RESOURCE_ID, value: insuranceRelation.id },
          ]);
        }
      }
    }

    setInsuranceSelectList((prevList) => {
      const filtered = prevList.filter((item) => item.insurance.id !== insuranceRelation.insurance.id);
      if (filtered.length === 0) {
        return [createNoInsuranceOption(patientSelected)];
      }
      return filtered;
    });
  };

  const updateInsurancePatientListHandler = async () => {
    if (!validateDataList()) {
      const realInsurances = insuranceSelectList.filter(
        (relation) => relation.insurance?.id !== NO_INSURANCE_OPTION_ID
      );

      if (realInsurances.length === 0) {
        return;
      }

      for (const relation of realInsurances) {
        const existsInBaseList = insuranceBaseList.some(
          (baseInsurance) => baseInsurance.id === relation.insurance.id
        );
        let method: Method;
        if (existsInBaseList) {
          method = "PUT";
        } else {
          method = "POST";
        }
        relation.patient = patientSelected;
        await sendRequest(
          CoreAPI_URLs.PATIENT_INSURANCE_GENERAL_OPERATION,
          null,
          method,
          JSON.stringify(relation),
          [{ header: HeaderEnum.RESOURCE_ID, value: relation.id }],
          { successMessage: t("insurancePatientTable.updated") }
        );
      }
    }
  };

  return (
    <Box sx={{ width: "100%", paddingTop: "2rem" }}>
      <Grid container spacing={2}>
        <PermissionWrapper
          requiredPermissions={[PermissionsUser[EntityPermissions.PATIENT_INSURANCE].CREATE]}
        >
          <Grid item xs={12} paddingBottom={"1rem"} id={"cy-InsuranceidFormContainer"}>
            <Autocomplete
              name="insurance.id"
              options={insuranceOptions}
              label={t("insurancePatientTable.selectInsurance")}
              formikOutside={true}
              onChange={(_e, value) => {
                if (value) {
                  onSelectInsuranceHandler(_e, value);
                }
              }}
              noOptionsText={t("insurancePatientTable.noOptions")}
            />
          </Grid>
        </PermissionWrapper>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead
                sx={{
                  whiteSpace: 'nowrap',
                  display: "table-header-group",
                  minWidth:100,
                  "& .MuiTableCell-head": {
                    color: darkMode ? "#fff" : "#000",
                  },
                  "& .MuiTableRow-root": {
                    color: darkMode ? "#fff" : "#000",
                  },
                }}
              >
                <TableRow>
                  <TableCell align="center">{t("insurancePatientTable.name")}</TableCell>
                  <TableCell align="center">{t("insurancePatientTable.state")}</TableCell>
                  <TableCell align="center">{t("insurancePatientTable.payerId")}</TableCell>
                  <TableCell align="center">{t("insurancePatientTable.coInsurance")}</TableCell>
                  <TableCell align="center">{t("insurancePatientTable.policyNo")}</TableCell>
                  <TableCell align="center">{t("insurancePatientTable.copay")}</TableCell>
                  <TableCell align="center">{t("insurancePatientTable.coverageLimit")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  display: "table-row-group",
                  "& .MuiTableCell-root": {
                    color: darkMode ? "#fff" : "#000",
                  },
                }}
              >
                {insuranceSelectList.map((relation) => {
                  const isNoInsurance = relation.insurance?.id === NO_INSURANCE_OPTION_ID;                
                  if (isNoInsurance) {
                    return (
                      <TableRow 
                        key={relation.insurance.id} 
                        sx={{ 
                          color: darkMode ? "#fff" : "#000",
                          backgroundColor: darkMode ? "#424242" : "#f5f5f5"
                        }}
                      >
                        <TableCell colSpan={7} align="center">
                          <Box sx={{ py: 2 }}>
                            <Box sx={{ fontSize: "1rem", fontWeight: 600, mb: 0.5 }}>
                              {t("insurancePatientTable.noInsurance")}
                            </Box>
                            <Box sx={{ fontSize: "0.875rem", fontStyle: "italic", color: darkMode ? "#bbb" : "#666" }}>
                              {t("insurancePatientTable.noInsuranceNote")}
                            </Box>
                          </Box>
                        </TableCell>
                        <PermissionWrapper
                          requiredPermissions={[
                            PermissionsUser[EntityPermissions.PATIENT_INSURANCE].DELETE,
                          ]}
                        >
                          <TableCell align="center">
                            <IconButton
                              type="button"
                              onClick={() => onDeleteInsuranceHandler(relation)}
                            >
                              <DeleteRoundedIcon />
                            </IconButton>
                          </TableCell>
                        </PermissionWrapper>
                      </TableRow>
                    );
                  }                
                  return (
                  <TableRow key={relation.insurance.id} sx={{ color: darkMode ? "#fff" : "#000" }}>
                    <TableCell align="center">{relation.insurance.name}</TableCell>
                    <TableCell align="center">{relation.insurance.state}</TableCell>
                    <TableCell align="center">{relation.insurance.payerId}</TableCell>
                    <TableCell id="cy-coInsurance" align="center">
                      <PercentageInput
                        value={relation.coInsurance == 0 ? '': relation.coInsurance}
                        onChange={(value) => handleCoInsuranceChange(value, relation)}
                        maxValue={100}
                        minValue={0}
                        decimals={2}
                        size="small"
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell id="cy-policy" align="center">
                      <TextField
                        type="text"
                        value={relation.policyId}
                        onChange={(event) => handlePolicyChange(event.target.value, relation)}
                        placeholder={t("insurancePatientTable.policyId") }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: isEmpty(relation.policyId) ? "red" : "",
                            },
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell id="cy-copay" align="center">
                      <TextField
                      type="number"
                        value={relation.coPay == 0 ? '': relation.coPay }
                        onChange={(event) => handleCopayChange(event.target.value, relation)}
                        placeholder="0"
                      />

                    </TableCell>
                    <TableCell id="cy-coverageLimit" align="center">
                      <TextField
                        type="number"
                        value={relation.coverageLimit == 0 ? '': relation.coverageLimit }
                        onChange={(event) => handleCoverageLimitChange(event.target.value, relation)}
                        placeholder="0"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: (relation.coverageLimit / relation.coverageLimit) != 1 ? "red" : "",
                            },
                          },
                        }}
                      />

                    </TableCell>
                    <PermissionWrapper
                      requiredPermissions={[
                        PermissionsUser[EntityPermissions.PATIENT_INSURANCE].DELETE,
                      ]}
                    >
                      <TableCell align="center">
                        <IconButton
                          type="button"
                          onClick={() => onDeleteInsuranceHandler(relation)}
                        >
                          <DeleteRoundedIcon />
                        </IconButton>
                      </TableCell>
                    </PermissionWrapper>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <PermissionWrapper
          requiredPermissions={[PermissionsUser[EntityPermissions.PATIENT_INSURANCE].EDIT]}
        >
          {patientSelected && (
            <Grid item xs={12} paddingBottom={"1rem"}>
              <Button
                type="button"
                color="success"
                onClick={() => updateInsurancePatientListHandler()}
              >
                {t("hpitemplateTable.update")}
              </Button>
            </Grid>
          )}
        </PermissionWrapper>
      </Grid>
    </Box>
  );
};

export default InsurancePatientTable;
